import React, { useContext, useEffect, useState } from "react";

import {
  Alert,
  Box,
  Grid,
  LinearProgress,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import clsx from "clsx";
import mainStyles from "styles";

import { AdminRestriction } from "@safetyculture/s12-apis-connect-web/s12/restrictions/admin/v1/restrictions_pb";
import {
  QuotaRestrictionConfiguration,
  RestrictionQuota,
} from "@safetyculture/s12-apis-connect-web/s12/restrictions/v1/common_pb";
import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";

import {
  getDocumentsAskAISettings,
  updateDocumentsAskAISettings,
} from "api/endpoints/documents-ai";
import { callOptionsHeaders } from "api/helpers";
import OrgAIUsageSection from "components/org-display/tabs/components/org-ai-usage-section";
import {
  BUCKET_OVERAGE,
  friendlyBucketLabel,
  orgQuotaBuckets,
  quotaUsage,
  useOrgAICredit,
  useOrgAICreditConfig,
} from "components/org-display/tabs/components/use-org-ai-credit";
import DisplayError from "components/shared/display-error";
import DisplayHeading from "components/shared/display-heading";
import { DEFAULT_SNACKBAR_DURATION, NO_DISPLAY_DATA } from "constants/index";
import { PermissionsContext } from "context/permissions-context";
import { GrpcError, SnackbarContext } from "context/snackbar-context";

interface OrgAITabProps {
  roleId: string;
  regionCode: string;
}

const TABLE_BORDER_STYLE = {
  borderWidth: "1px",
  borderStyle: "solid",
  borderRadius: "5px",
  margin: "20px",
} as const;

const LoadingStateTable = () => (
  <Table size="medium">
    <TableBody>
      <TableRow>
        <TableCell colSpan={2}>
          <LinearProgress />
        </TableCell>
      </TableRow>
    </TableBody>
  </Table>
);

interface AISwitchCellProps {
  askAIEnabled: boolean;
  canUpdateAISettings: boolean;
  isUpdating: boolean;
  onToggle: (enabled: boolean) => void;
  classes: ReturnType<typeof mainStyles>;
}

const AISwitchCell = ({
  askAIEnabled,
  isUpdating,
  onToggle,
  canUpdateAISettings,
  classes,
}: AISwitchCellProps) => (
  <TableCell
    className={clsx(
      classes.tableCell,
      classes.leftCell,
      classes.tableRightColPadding
    )}
    align="left"
    style={{ fontWeight: "normal", width: "70%" }}
  >
    <Box display="flex" alignItems="center" gap={2}>
      <Switch
        checked={askAIEnabled}
        onChange={(event) => onToggle(event.target.checked)}
        disabled={!canUpdateAISettings || isUpdating}
        data-testid="documents-ask-ai-switch"
      />
      <Typography variant="body2" color="text.secondary">
        {askAIEnabled
          ? "Allow users to use AI Assistant and enable document ingestion."
          : "AI questions about documents are disabled"}
      </Typography>
    </Box>
  </TableCell>
);

const QuotaBar = ({ quota }: { quota: RestrictionQuota }) => {
  const used = quotaUsage(quota);
  const unit = quota.unit || "credits";
  const hasLimit = quota.limit > 0;

  if (!hasLimit) {
    return <>{`${used} ${unit} used`}</>;
  }

  const pct = Math.min((used / quota.limit) * 100, 100);
  return (
    <Box>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{ borderRadius: 1, height: 6, mb: 0.5 }}
      />
      <Typography variant="caption" color="text.secondary">
        {used} / {quota.limit} {unit} used · {quota.remaining} remaining
      </Typography>
    </Box>
  );
};

interface AICreditUsageRowsProps {
  isLoading: boolean;
  orgRestriction?: AdminRestriction;
  classes: ReturnType<typeof mainStyles>;
}

const AICreditUsageRows = ({
  isLoading,
  orgRestriction,
  classes,
}: AICreditUsageRowsProps) => {
  const labelCellClasses = clsx(
    classes.tableCell,
    classes.leftCell,
    classes.tableLeftColPadding
  );
  const valueCellClasses = clsx(
    classes.tableCell,
    classes.leftCell,
    classes.tableRightColPadding
  );

  const renderRow = (key: string, label: string, value: React.ReactNode) => (
    <TableRow key={key} data-testid={`org-quota-${key}`}>
      <TableCell
        className={labelCellClasses}
        align="right"
        style={{ width: "30%" }}
      >
        <Typography variant="body1" fontWeight="medium">
          {label}
        </Typography>
      </TableCell>
      <TableCell
        className={valueCellClasses}
        align="left"
        style={{ fontWeight: "normal", width: "70%" }}
      >
        {value}
      </TableCell>
    </TableRow>
  );

  if (isLoading) {
    return renderRow("loading", "Credit usage", <LinearProgress />);
  }

  // Lead with the overage bucket; that is the org's billable usage.
  const buckets = [...orgQuotaBuckets(orgRestriction)].sort((a, b) =>
    a.key === BUCKET_OVERAGE ? -1 : b.key === BUCKET_OVERAGE ? 1 : 0
  );

  if (buckets.length === 0) {
    return renderRow(
      "empty",
      "Credit usage",
      <Typography variant="body2" color="text.secondary">
        No AI credit quota configured for this organisation.
      </Typography>
    );
  }

  return (
    <>
      {buckets.map(({ key, label, quota }) =>
        renderRow(
          key,
          key === BUCKET_OVERAGE ? "Overage" : `${label} usage`,
          <QuotaBar quota={quota} />
        )
      )}
    </>
  );
};

const formatConfigPeriod = (config: QuotaRestrictionConfiguration): string => {
  const start = config.currentPeriodStart?.toDate();
  const end = config.currentPeriodEnd?.toDate();
  if (!start || !end) return NO_DISPLAY_DATA;
  return `${start.toLocaleDateString()} – ${end.toLocaleDateString()}`;
};

const formatConfigAllowances = (
  config: QuotaRestrictionConfiguration
): string => {
  if (config.allowances.length === 0) return NO_DISPLAY_DATA;
  return config.allowances
    .map(
      ({ key, amount, unit }) =>
        `${friendlyBucketLabel(key)}: ${amount} ${unit || "credits"}`
    )
    .join(" · ");
};

interface AICreditConfigRowsProps {
  isLoading: boolean;
  config?: QuotaRestrictionConfiguration;
  classes: ReturnType<typeof mainStyles>;
}

const AICreditConfigRows = ({
  isLoading,
  config,
  classes,
}: AICreditConfigRowsProps) => {
  const labelCellClasses = clsx(
    classes.tableCell,
    classes.leftCell,
    classes.tableLeftColPadding
  );
  const valueCellClasses = clsx(
    classes.tableCell,
    classes.leftCell,
    classes.tableRightColPadding
  );

  const renderRow = (key: string, label: string, value: React.ReactNode) => (
    <TableRow key={key} data-testid={`org-config-${key}`}>
      <TableCell
        className={labelCellClasses}
        align="right"
        style={{ width: "30%" }}
      >
        <Typography variant="body1" fontWeight="medium">
          {label}
        </Typography>
      </TableCell>
      <TableCell
        className={valueCellClasses}
        align="left"
        style={{ fontWeight: "normal", width: "70%" }}
      >
        {value}
      </TableCell>
    </TableRow>
  );

  if (isLoading) {
    return renderRow(
      "config-loading",
      "Plan configuration",
      <LinearProgress />
    );
  }

  if (!config) return null;

  return (
    <>
      {renderRow("credit-period", "Credit period", formatConfigPeriod(config))}
      {renderRow(
        "credit-allowances",
        "Credit allowances",
        formatConfigAllowances(config)
      )}
    </>
  );
};

const OrgAITab = ({ roleId, regionCode }: OrgAITabProps) => {
  const theme = useTheme();
  const classes = mainStyles();
  const { showError } = useContext(SnackbarContext);
  const { hasPermissions, permissions } = useContext(PermissionsContext);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [askAIEnabled, setAskAIEnabled] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [error, setError] = useState<string>("");
  const canUpdateAISettings =
    !permissions.isPermissionsLoading &&
    hasPermissions([ActionType.UPDATE_DOCUMENTS_ASK_AI_SETTINGS]);
  const canViewAIUsage =
    !permissions.isPermissionsLoading &&
    hasPermissions([ActionType.LIST_ORG_USER_RESTRICTIONS]);
  const canViewAIConfig =
    !permissions.isPermissionsLoading &&
    hasPermissions([ActionType.GET_ORG_RESTRICTION_CONFIGURATION]);

  const {
    isLoading: isQuotaLoading,
    isReady: isUsageReady,
    orgRestriction,
    usageWindow,
  } = useOrgAICredit(roleId, regionCode, canViewAIUsage);

  const { isLoading: isConfigLoading, config: aiCreditConfig } =
    useOrgAICreditConfig(roleId, regionCode, canViewAIConfig);

  const loadAISettings = async () => {
    setIsLoading(true);
    setError("");

    try {
      const response = await getDocumentsAskAISettings(
        { orgId: roleId },
        callOptionsHeaders(regionCode, roleId)
      );
      setAskAIEnabled(response.enabled);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      setError(errorMessage);
      showError(err as GrpcError);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleAskAI = async (enabled: boolean) => {
    setIsUpdating(true);
    setError("");

    try {
      await updateDocumentsAskAISettings(
        { orgId: roleId, enabled },
        callOptionsHeaders(regionCode, roleId)
      );
      setAskAIEnabled(enabled);
      setSuccessMessage(
        `AI Assistant has been ${enabled ? "enabled" : "disabled"}`
      );
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      setError(errorMessage);
      showError(err as GrpcError);
      // Revert the checkbox state on error
      setAskAIEnabled(!enabled);
    } finally {
      setIsUpdating(false);
    }
  };

  useEffect(() => {
    loadAISettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Grid container>
      <Grid item xs={12} sx={classes.paddingTopSmall}>
        <DisplayHeading>AI SETTINGS</DisplayHeading>
        <DisplayError errorMessage={error} align="center" />
      </Grid>
      <Grid item xs={12} sx={classes.paddingTopSmall} />
      <Grid item xs={12}>
        <Box
          style={{
            borderColor: theme.palette.divider,
            ...TABLE_BORDER_STYLE,
          }}
        >
          {isLoading ? (
            <LoadingStateTable />
          ) : (
            <Table size="medium">
              <TableBody>
                <TableRow>
                  <TableCell
                    className={clsx(
                      classes.tableCell,
                      classes.leftCell,
                      classes.tableLeftColPadding
                    )}
                    align="right"
                    style={{ width: "30%" }}
                  >
                    <Typography variant="body1" fontWeight="medium">
                      AI Assistant
                    </Typography>
                  </TableCell>
                  <AISwitchCell
                    askAIEnabled={askAIEnabled}
                    canUpdateAISettings={canUpdateAISettings}
                    isUpdating={isUpdating}
                    onToggle={handleToggleAskAI}
                    classes={classes}
                  />
                </TableRow>
                {canViewAIUsage && (
                  <AICreditUsageRows
                    isLoading={isQuotaLoading}
                    orgRestriction={orgRestriction}
                    classes={classes}
                  />
                )}
                {canViewAIConfig && (
                  <AICreditConfigRows
                    isLoading={isConfigLoading}
                    config={aiCreditConfig}
                    classes={classes}
                  />
                )}
              </TableBody>
            </Table>
          )}
        </Box>
      </Grid>

      {canViewAIUsage && (
        <Grid item xs={12} sx={classes.paddingTopSmall}>
          <OrgAIUsageSection
            roleId={roleId}
            regionCode={regionCode}
            usageWindow={usageWindow}
            ready={isUsageReady}
          />
        </Grid>
      )}

      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={!!successMessage}
        autoHideDuration={DEFAULT_SNACKBAR_DURATION}
        onClose={() => setSuccessMessage("")}
        data-testid="ai-settings-success-snackbar"
      >
        <Alert severity="success" variant="filled">
          {successMessage}
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default OrgAITab;
